import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import type { CartItem, WooProduct } from "../types";
import {
  clampCartItemQuantity,
  clampQuantity,
  snapshotStockFromProduct,
} from "../utils/stockHelpers";

type StoreCarts = Record<string, CartItem[]>;

function parseProductPrice(product: WooProduct): number {
  const raw = product.onSale && product.salePrice
    ? product.salePrice
    : product.price || product.regularPrice;
  const num = parseFloat(raw);
  return Number.isNaN(num) ? 0 : num;
}

function warnIfCapped(capped: boolean, maxAllowed: number | null) {
  if (capped && maxAllowed !== null) {
    toast.warning(`Estoque máximo: ${maxAllowed} unidades`);
  }
}

/** Carrinho local separado por loja (`storeId`). */
export function useWooCommerceCart() {
  const [carts, setCarts] = useState<StoreCarts>({});

  const getCartItems = useCallback(
    (storeId: string): CartItem[] => carts[storeId] ?? [],
    [carts],
  );

  const addToCart = useCallback(
    (storeId: string, product: WooProduct, quantity = 1) => {
      const price = parseProductPrice(product);
      const imageSrc = product.images[0]?.thumbnail ?? product.images[0]?.src;
      const stockSnapshot = snapshotStockFromProduct(product);

      setCarts((prev) => {
        const current = prev[storeId] ?? [];
        const existing = current.find((item) => item.productId === product.id);
        const targetQty = existing ? existing.quantity + quantity : quantity;
        const { quantity: clamped, capped, maxAllowed } = clampQuantity(
          product,
          targetQty,
        );

        warnIfCapped(capped, maxAllowed);
        if (clamped <= 0) return prev;

        if (existing) {
          return {
            ...prev,
            [storeId]: current.map((item) =>
              item.productId === product.id
                ? { ...item, quantity: clamped, ...stockSnapshot }
                : item,
            ),
          };
        }

        return {
          ...prev,
          [storeId]: [
            ...current,
            {
              productId: product.id,
              name: product.name,
              price,
              quantity: clamped,
              imageSrc,
              ...stockSnapshot,
            },
          ],
        };
      });
    },
    [],
  );

  const removeFromCart = useCallback((storeId: string, productId: number) => {
    setCarts((prev) => ({
      ...prev,
      [storeId]: (prev[storeId] ?? []).filter(
        (item) => item.productId !== productId,
      ),
    }));
  }, []);

  const updateQuantity = useCallback(
    (storeId: string, productId: number, quantity: number) => {
      setCarts((prev) => {
        const current = prev[storeId] ?? [];
        const item = current.find((entry) => entry.productId === productId);
        if (!item) return prev;

        const { quantity: clamped, capped, maxAllowed } = clampCartItemQuantity(
          item,
          quantity,
        );
        warnIfCapped(capped, maxAllowed);

        if (clamped <= 0) {
          return {
            ...prev,
            [storeId]: current.filter((entry) => entry.productId !== productId),
          };
        }

        return {
          ...prev,
          [storeId]: current.map((entry) =>
            entry.productId === productId
              ? { ...entry, quantity: clamped }
              : entry,
          ),
        };
      });
    },
    [],
  );

  const clearCart = useCallback((storeId: string) => {
    setCarts((prev) => {
      const next = { ...prev };
      delete next[storeId];
      return next;
    });
  }, []);

  const getSubtotal = useCallback(
    (storeId: string): number => {
      return getCartItems(storeId).reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
      );
    },
    [getCartItems],
  );

  const getTotalItemCount = useCallback(
    (storeId: string): number => {
      return getCartItems(storeId).reduce((sum, item) => sum + item.quantity, 0);
    },
    [getCartItems],
  );

  const getTotalItems = getTotalItemCount;

  const isInCart = useCallback(
    (storeId: string, productId: number): boolean => {
      return getCartItems(storeId).some((item) => item.productId === productId);
    },
    [getCartItems],
  );

  const getItemQuantity = useCallback(
    (storeId: string, productId: number): number => {
      return (
        getCartItems(storeId).find((item) => item.productId === productId)
          ?.quantity ?? 0
      );
    },
    [getCartItems],
  );

  const storesWithItems = useMemo(
    () => Object.keys(carts).filter((id) => (carts[id]?.length ?? 0) > 0),
    [carts],
  );

  const getTotalItemsAllStores = useCallback((): number => {
    return Object.values(carts).reduce(
      (sum, items) =>
        sum + items.reduce((itemSum, item) => itemSum + item.quantity, 0),
      0,
    );
  }, [carts]);

  const getSubtotalAllStores = useCallback((): number => {
    return Object.values(carts).reduce(
      (sum, items) =>
        sum +
        items.reduce(
          (itemSum, item) => itemSum + item.price * item.quantity,
          0,
        ),
      0,
    );
  }, [carts]);

  return {
    getCartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getSubtotal,
    getTotalItemCount,
    getTotalItems,
    getTotalItemsAllStores,
    getSubtotalAllStores,
    isInCart,
    getItemQuantity,
    storesWithItems,
    carts,
  };
}
